package org.teddapp.project.sync

import com.tedd.db.TeddDatabase
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put
import org.teddapp.project.db.repo.UserProfileRepository
import org.teddapp.project.server.ServerException
import org.teddapp.project.server.ServerSession

/** Nom de l'entité côté serveur. Une seule table locale a gardé un nom en chameau. */
internal val EntityKind.serverName: String get() = if (this == EntityKind.ProjectStatus) "project_status" else tableName

/**
 * Identifiants. Le serveur veut des UUID ; les lignes créées avant lui ont un identifiant
 * local court. On en dérive un UUID stable (le même sur tout appareil pour le même
 * identifiant), et on retient d'où il vient pour le reconnaître au retour.
 */
internal object ServerIds {
    private val UUID = Regex("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$")

    fun isUuid(value: String): Boolean = UUID.matches(value)

    /** UUID (version 8, « sur mesure ») dérivé d'un identifiant local : 128 bits d'empreinte. */
    fun derive(localId: String): String {
        val bytes = localId.encodeToByteArray()
        val hex = hex16(mix(fnv(bytes, -0x340d631b7bdddcdbL))) + hex16(mix(fnv(bytes, 0x6c62272e07bb0142L)))
        val variant = "89ab"[hex[16].digitToInt(16) and 3]
        return "${hex.substring(0, 8)}-${hex.substring(8, 12)}-8${hex.substring(13, 16)}-$variant${hex.substring(17, 20)}-${hex.substring(20, 32)}"
    }

    private fun fnv(bytes: ByteArray, seed: Long): Long {
        var h = seed
        for (b in bytes) { h = h xor (b.toLong() and 0xff); h *= 0x100000001b3L }
        return h
    }

    // Finition de MurmurHash3 : répartit les bits d'une empreinte trop régulière.
    private fun mix(value: Long): Long {
        var h = value
        h = h xor (h ushr 33); h *= -0xae502812aa7333L
        h = h xor (h ushr 33); h *= -0x3b314601e57a13adL
        return h xor (h ushr 33)
    }

    private fun hex16(value: Long): String = value.toULong().toString(16).padStart(16, '0')
}

/**
 * Le rang d'une ligne : un entier côté application, un texte ordonnable côté serveur.
 * Douze chiffres complétés de zéros gardent le même ordre dans les deux mondes.
 */
/**
 * Le rang d'une liste, en clé texte qui se trie comme le nombre. Les rangs positifs font
 * « p » suivi de douze chiffres ; les négatifs, que l'application emploie pour mettre une
 * ligne en tête, font « n » suivi du complément : « n » passe avant « p », et -1000 avant -500.
 */
internal object Positions {
    private const val MAX = 999_999_999_999L
    private val SHAPE = Regex("^[np]\\d{12}$")
    fun encode(orderIndex: Long): String {
        val v = orderIndex.coerceIn(-MAX, MAX)
        return if (v >= 0) "p" + v.toString().padStart(12, '0') else "n" + (MAX + v).toString().padStart(12, '0')
    }
    fun decode(position: String?): Long {
        if (position == null || !SHAPE.matches(position)) return 0L
        val digits = position.substring(1).toLong()
        return if (position[0] == 'p') digits else digits - MAX
    }
}

/**
 * Traduit les lignes de l'application dans le format d'échange du serveur, et retour.
 * Sans réseau ni base : tout ce qui dépend de l'extérieur lui est passé en fonctions.
 */
internal class WireCodec(
    private val toServerId: (String) -> String,
    private val toLocalId: (String) -> String,
) {
    private val json = Json

    private fun localKey(entity: String, f: WireField): String = when {
        // Le rang d'une liste s'appelle « order_index » en local ; un simple entier
        // nommé « position » (parcours_step) garde son nom.
        f.name == "position" && f.type == WireType.Text -> "order_index"
        // Le serveur nomme « solde d'ouverture » ce que l'application tient comme solde.
        entity == "finance_account" && f.name == "opening_balance" -> "balance"
        else -> f.name
    }

    /** Les champs d'une ligne, prêts pour un `upsert`. Un champ que l'application ne porte pas est laissé au serveur. */
    fun encode(entity: EntityKind, row: RemoteRow): JsonObject {
        val name = entity.serverName
        val fields = SyncRegistry.fields.getValue(name)
        return buildJsonObject {
            for (f in fields) {
                val key = localKey(name, f)
                if (key !in row.payload) continue
                // Le rang d'une liste voyage en clé texte ; mais « position » est un simple entier
                // là où le registre le dit (parcours_step), et part alors tel quel.
                val rank = f.name == "position" && f.type == WireType.Text
                put(f.name, if (rank) JsonPrimitive(Positions.encode(asLong(row.payload[key]) ?: 0L)) else toWire(f, row.payload[key], row))
            }
        }
    }

    private fun asLong(v: Any?): Long? = when (v) {
        is Number -> v.toLong()
        is Boolean -> if (v) 1L else 0L
        is String -> v.toLongOrNull()
        else -> null
    }

    private fun toWire(f: WireField, v: Any?, row: RemoteRow): JsonElement {
        if (v == null) {
            if (f.nullable) return JsonNull
            // Champ obligatoire côté serveur, vide ici : la valeur neutre de son type.
            return when (f.type) {
                WireType.Text -> JsonPrimitive("")
                WireType.Int -> JsonPrimitive(0L)
                WireType.Bool -> JsonPrimitive(false)
                WireType.Instant -> JsonPrimitive(row.updatedAt)
                WireType.Json -> JsonArray(emptyList())
                WireType.Date, WireType.Uuid -> JsonNull
            }
        }
        return when (f.type) {
            WireType.Uuid -> JsonPrimitive(toServerId(v.toString()))
            WireType.Text, WireType.Date -> JsonPrimitive(v.toString())
            WireType.Int, WireType.Instant -> asLong(v)?.let { JsonPrimitive(it) } ?: JsonNull
            WireType.Bool -> JsonPrimitive(when (v) { is Boolean -> v; is Number -> v.toLong() != 0L; else -> v.toString() == "true" })
            WireType.Json -> jsonValue(v.toString())
        }
    }

    /**
     * Un texte local qui est un tableau ou un objet JSON voyage tel quel. Les étiquettes
     * écrites à la main (« ingénierie, données ») deviennent un tableau de chaînes : un
     * mot nu n'est pas du JSON, et l'envoyer brut rendrait tout le lot illisible.
     */
    private fun jsonValue(text: String): JsonElement {
        val trimmed = text.trim()
        if (trimmed.isEmpty()) return JsonArray(emptyList())
        if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            runCatching { json.parseToJsonElement(trimmed) }.getOrNull()?.let { return it }
        }
        return JsonArray(trimmed.split(',').map { it.trim() }.filter { it.isNotEmpty() }.map { JsonPrimitive(it) })
    }

    /** Une ligne rendue par le pull, dans la forme que le moteur applique. */
    fun decode(entity: EntityKind, localId: String, change: JsonObject): RemoteRow {
        val name = entity.serverName
        val fields = SyncRegistry.fields.getValue(name)
        val values = change["fields"]?.jsonObject ?: JsonObject(emptyMap())
        val payload = buildMap<String, Any?> {
            for (f in fields) put(localKey(name, f), fromWire(f, values[f.name], name))
        }
        val deletedAt = (change["deleted_at"] as? JsonPrimitive)?.longOrNull
        // L'estampille porte les millisecondes dans ses bits de poids fort.
        val hlcMs = ((change["hlc"] as? JsonPrimitive)?.longOrNull ?: 0L) shr HLC_COUNTER_BITS
        return RemoteRow(
            id = localId,
            updatedAt = maxOf(hlcMs, deletedAt ?: 0L),
            isDeleted = deletedAt != null,
            deletedAt = deletedAt,
            payload = payload,
            receivedSeq = (change["version"] as? JsonPrimitive)?.longOrNull,
        )
    }

    /** Les étiquettes sont du texte « a, b » en local ; les autres champs JSON y restent du JSON. */
    private fun textList(entity: String, f: WireField) = f.name == "tags" && entity in setOf("note", "skill")

    private fun fromWire(f: WireField, el: JsonElement?, entity: String): Any? {
        if (el == null || el is JsonNull) return null
        if (f.name == "position" && f.type == WireType.Text) return Positions.decode((el as? JsonPrimitive)?.contentOrNull)
        return when (f.type) {
            WireType.Uuid -> (el as? JsonPrimitive)?.contentOrNull?.let(toLocalId)
            WireType.Text, WireType.Date -> (el as? JsonPrimitive)?.contentOrNull
            WireType.Int, WireType.Instant -> (el as? JsonPrimitive)?.longOrNull
            WireType.Bool -> (el as? JsonPrimitive)?.booleanOrNull?.let { if (it) 1L else 0L }
            // Un tableau de chaînes redevient le texte local « a, b » ; le reste reste du JSON.
            WireType.Json -> when {
                el is JsonPrimitive && el.isString -> el.content
                textList(entity, f) && el is JsonArray -> el.joinToString(", ") { (it as? JsonPrimitive)?.contentOrNull ?: it.toString() }
                else -> el.toString()
            }
        }
    }

    companion object {
        const val HLC_COUNTER_BITS = 16
    }
}

/**
 * Le serveur teddApp, vu par le moteur de synchronisation.
 *
 * Envoi : chaque ligne devient une mutation (`upsert` ou `delete`), estampillée de sa
 * date de modification. Réception : le serveur n'a qu'un curseur, toutes entités
 * confondues ; on tire donc tout d'un coup ([pullAll]).
 */
class RustRemoteStore(
    private val session: ServerSession,
    private val db: TeddDatabase,
) : RemoteStore {

    private val byServerName = EntityKind.entries.associateBy { it.serverName }

    private val codec = WireCodec(
        toServerId = { local ->
            if (ServerIds.isUuid(local)) local.lowercase()
            else ServerIds.derive(local).also { db.serverIdMapQueries.remember(it, local) }
        },
        toLocalId = { server -> db.serverIdMapQueries.lookup(server).executeAsOneOrNull() ?: server },
    )

    // Un compte n'a qu'un profil et qu'un minuteur : le serveur les range sous l'identifiant du compte.
    private fun serverId(entity: EntityKind, localId: String): String = when (entity) {
        EntityKind.UserProfile, EntityKind.TimerState -> session.userId
        else -> if (ServerIds.isUuid(localId)) localId.lowercase() else ServerIds.derive(localId).also { db.serverIdMapQueries.remember(it, localId) }
    }

    private fun localId(entity: EntityKind, serverId: String): String = when (entity) {
        EntityKind.UserProfile -> UserProfileRepository.SINGLETON_ID
        EntityKind.TimerState -> TIMER_ID
        else -> db.serverIdMapQueries.lookup(serverId).executeAsOneOrNull() ?: serverId
    }

    override suspend fun pull(entity: EntityKind, since: Long): List<RemoteRow> = emptyList()

    @OptIn(ExperimentalUuidApi::class)
    override suspend fun push(entity: EntityKind, rows: List<RemoteRow>) {
        if (rows.isEmpty()) return
        // Une ligne supprimée part en deux temps : son contenu, puis sa suppression. Le
        // serveur la garde ainsi comme suppression logique, et ses enfants encore vivants
        // (une transaction d'un compte fermé, un jalon d'un objectif retiré) trouvent leur
        // parent. Un simple « delete » d'une ligne qu'il n'a jamais vue ne créerait rien.
        val sent = linkedMapOf<String, RemoteRow>()
        val mutations = buildJsonArray {
            for (row in rows) {
                val id = serverId(entity, row.id)
                val hlc = row.updatedAt shl WireCodec.HLC_COUNTER_BITS
                if (row.isDeleted) {
                    val upsertId = Uuid.random().toString().also { sent[it] = row }
                    add(buildJsonObject {
                        put("mutation_id", upsertId); put("entity", entity.serverName); put("id", id)
                        put("hlc", hlc); put("op", "upsert"); put("fields", codec.encode(entity, row))
                    })
                }
                val mutationId = Uuid.random().toString().also { sent[it] = row }
                add(buildJsonObject {
                    put("mutation_id", mutationId)
                    put("entity", entity.serverName)
                    put("id", id)
                    // La suppression suit le contenu d'un cran d'horloge : elle doit gagner.
                    put("hlc", if (row.isDeleted) hlc + 1 else hlc)
                    if (row.isDeleted) put("op", "delete")
                    else { put("op", "upsert"); put("fields", codec.encode(entity, row)) }
                })
                // Une ligne vivante qui garde sa date de suppression sort de la corbeille. Le
                // serveur ne réveille pas une ligne supprimée sur une simple modification : il
                // lui faut une restauration, un cran d'horloge après le contenu.
                if (!row.isDeleted && row.deletedAt != null) {
                    val restoreId = Uuid.random().toString().also { sent[it] = row }
                    add(buildJsonObject {
                        put("mutation_id", restoreId); put("entity", entity.serverName); put("id", id)
                        put("hlc", hlc + 1); put("op", "restore")
                    })
                }
            }
        }
        val outcome = session.authed { api, access -> api.push(access, mutations) }
        val refused = outcome["acks"]?.jsonArray.orEmpty().mapNotNull { ack ->
            val o = ack.jsonObject
            if (o["result"]?.jsonPrimitive?.contentOrNull != "rejected") return@mapNotNull null
            val row = sent[o["mutation_id"]?.jsonPrimitive?.contentOrNull] ?: return@mapNotNull null
            val reason = o["reason"]?.jsonPrimitive?.contentOrNull ?: "invalid"
            // Supprimer une ligne que le serveur n'a jamais eue : rien à faire, ce n'est pas un échec.
            if (row.isDeleted && reason == "not_found") null
            // Une ressource refusée (doublon, quota) reste sur l'appareil avec sa raison :
            // l'écran la montre, et la synchronisation des autres lignes continue.
            else if (keepRefused(entity, row, reason)) null
            else row.id to reason
        }
        if (refused.isNotEmpty()) {
            val (id, reason) = refused.first()
            throw IllegalStateException("${refused.size} ligne(s) refusée(s) par le serveur : ${reasonText(reason)} (par exemple $id)")
        }
    }

    /** Garde sur l'appareil le refus d'une ressource, ou du fichier d'une ressource. Rend true s'il est gardé. */
    private fun keepRefused(entity: EntityKind, row: RemoteRow, reason: String): Boolean {
        if (entity == EntityKind.AssistantConversation && !row.isDeleted) {
            AssistantSyncOps.markRefused(db, row.id, reason)
            return true
        }
        val resource = when {
            row.isDeleted -> return false
            entity == EntityKind.SkillResource -> row.id
            entity == EntityKind.Attachment && row.payload["owner_kind"] == SkillResourceSyncOps.OWNER -> row.payload["owner_id"] as? String
            else -> null
        } ?: return false
        SkillResourceSyncOps.markRefused(db, resource, reason, org.teddapp.project.db.nowMillis())
        return true
    }

    private fun reasonText(reason: String) = when (reason) {
        "invalid" -> "valeur invalide"
        "missing_parent" -> "parent absent du serveur"
        "cycle" -> "boucle dans une arborescence"
        "not_found" -> "ligne inconnue"
        "unknown_entity" -> "entité inconnue"
        "quota_exceeded" -> "limite atteinte"
        "duplicate" -> "doublon"
        else -> reason
    }

    override suspend fun pullAll(since: Long): GlobalPull = try {
        readAll(since)
    } catch (e: ServerException) {
        // Le serveur ne peut plus servir ce curseur (longue absence, base restaurée) : on reprend de zéro.
        if (e.code == "resync_required" && since != 0L) readAll(0L) else throw e
    }

    private suspend fun readAll(since: Long): GlobalPull {
        val rows = mutableMapOf<EntityKind, MutableList<RemoteRow>>()
        var after = since
        while (true) {
            val page = session.authed { api, access -> api.pull(access, after, PAGE) }
            for (change in page["changes"]?.jsonArray.orEmpty()) {
                val o = change.jsonObject
                // Une entité que cette version de l'application ne connaît pas : on la laisse au serveur.
                val entity = byServerName[o["entity"]?.jsonPrimitive?.contentOrNull] ?: continue
                val id = o["id"]?.jsonPrimitive?.contentOrNull ?: continue
                if (entity == EntityKind.PomodoroSession) adoptDaySession(id, o)
                rows.getOrPut(entity) { mutableListOf() } += codec.decode(entity, localId(entity, id), o)
            }
            after = page["next_cursor"]?.jsonPrimitive?.longOrNull ?: after
            if (page["has_more"]?.jsonPrimitive?.booleanOrNull != true) break
        }
        return GlobalPull(rows, after)
    }

    /**
     * La journée de Focus porte un identifiant tiré de sa date, le même sur tous les
     * appareils. Reçue d'un autre appareil avant que celui-ci ait envoyé la sienne, elle
     * doit retomber sur la ligne locale du même jour, pas en ouvrir une seconde : on
     * retient la correspondance, que ses tâches suivront.
     */
    private fun adoptDaySession(serverId: String, change: kotlinx.serialization.json.JsonObject) {
        val date = change["fields"]?.jsonObject?.get("date_start")?.jsonPrimitive?.contentOrNull ?: return
        val local = "pom-day-$date"
        if (ServerIds.derive(local) == serverId.lowercase()) db.serverIdMapQueries.remember(serverId.lowercase(), local)
    }

    companion object {
        private const val PAGE = 1000
        private const val TIMER_ID = "current"
    }
}
